import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer, Legend } from 'recharts';
import { CTAButtonLight } from '../ui/CTAButton';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.55,
      delay: i * 0.12,
      ease: 'easeOut'
    }
  })
};
const ordinalLabels = ['1st Mo', '2nd Mo', '3rd Mo', '4th Mo', '5th Mo', '6th Mo', '7th Mo', '8th Mo', '9th Mo', '10th Mo', '11th Mo', '12th Mo'];
const data = [{
  month: '1st Mo',
  withSystem: 40000,
  withoutSystem: 10000
}, {
  month: '2nd Mo',
  withSystem: 75000,
  withoutSystem: 10000
}, {
  month: '3rd Mo',
  withSystem: 115000,
  withoutSystem: 9000
}, {
  month: '4th Mo',
  withSystem: 165000,
  withoutSystem: 9000
}, {
  month: '5th Mo',
  withSystem: 230000,
  withoutSystem: 8500
}, {
  month: '6th Mo',
  withSystem: 310000,
  withoutSystem: 8000
}, {
  month: '7th Mo',
  withSystem: 400000,
  withoutSystem: 7500
}, {
  month: '8th Mo',
  withSystem: 510000,
  withoutSystem: 7000
}, {
  month: '9th Mo',
  withSystem: 630000,
  withoutSystem: 6500
}, {
  month: '10th Mo',
  withSystem: 770000,
  withoutSystem: 6000
}, {
  month: '11th Mo',
  withSystem: 900000,
  withoutSystem: 5500
}, {
  month: '12th Mo',
  withSystem: 1050000,
  withoutSystem: 5000
}];
const formatYAxis = (value: number) => {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value}`;
};
const CustomTooltip = ({
  active,
  payload,
  label
}: any) => {
  if (active && payload && payload.length) {
    return <div className="bg-white border border-[#1A2B4A]/10 rounded-xl p-3 shadow-lg text-sm" data-id="element-568">
        <p className="font-bold text-[#1A2B4A] mb-1" data-id="element-569">{label}</p>
        {payload.map((entry: any) => <p key={entry.name} style={{
        color: entry.color
      }} className="font-medium" data-id="element-570">
            {entry.name === 'withSystem' ? 'With System' : 'Without System'}:{' '}
            {formatYAxis(entry.value)}
          </p>)}
      </div>;
  }
  return null;
};
export function RevenueProjectionSection() {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    amount: 0.2
  });
  return <section className="w-full bg-[#F5F7FA] py-24 px-6" data-id="element-571">
      <div ref={ref} className="max-w-5xl mx-auto" data-id="element-572">
        <motion.div custom={0} variants={fadeUp} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="text-center mb-14" data-id="element-573">
          <span className="inline-block bg-[#4ECDC4] text-[#1A2B4A] text-xs font-bold uppercase px-3 py-1 rounded-full mb-4 tracking-wider" data-id="element-574">
            Revenue Impact
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-[#1A2B4A] mb-4 leading-tight" data-id="element-575">
            What 20+ New Reps Per Month Means for Your Revenue
          </h2>
        </motion.div>

        <motion.div custom={1} variants={fadeUp} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="bg-white rounded-2xl shadow-md p-4 sm:p-8 mb-8" data-id="element-576">
          {/* Mobile chart: simplified, centered, fewer labels */}
          <div className="sm:hidden" data-id="element-577">
            <ResponsiveContainer width="100%" height={260} data-id="element-578">
              <LineChart data={data} margin={{
              top: 20,
              right: 16,
              left: 0,
              bottom: 8
            }} data-id="element-579">
                <CartesianGrid strokeDasharray="3 3" stroke="#1A2B4A10" data-id="element-580" />
                <XAxis dataKey="month" tick={{
                fill: '#1A2B4A80',
                fontSize: 10
              }} axisLine={false} tickLine={false} interval={2} data-id="element-581" />
                <YAxis tickFormatter={formatYAxis} tick={{
                fill: '#1A2B4A80',
                fontSize: 10
              }} axisLine={false} tickLine={false} width={40} data-id="element-582" />
                <Tooltip content={<CustomTooltip data-id="element-584" />} data-id="element-583" />
                <Line type="monotone" dataKey="withSystem" stroke="#4ECDC4" strokeWidth={3} dot={false} name="withSystem" data-id="element-585" />
                <Line type="monotone" dataKey="withoutSystem" stroke="#1A2B4A40" strokeWidth={1.5} strokeDasharray="4 3" dot={false} name="withoutSystem" data-id="element-586" />
              </LineChart>
            </ResponsiveContainer>
            <div className="flex items-center justify-center gap-4 mt-3" data-id="element-587">
              <div className="flex items-center gap-1.5" data-id="element-588">
                <div className="w-5 h-0.5 bg-[#4ECDC4]" data-id="element-589" />
                <span className="text-xs text-[#1A2B4A]/60" data-id="element-590">With System</span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-591">
                <div className="w-5 h-0" style={{
                borderTop: '2px dashed #1A2B4A60'
              }} data-id="element-592" />
                <span className="text-xs text-[#1A2B4A]/60" data-id="element-593">Without</span>
              </div>
            </div>
          </div>

          {/* Desktop chart: full labels */}
          <div className="hidden sm:block" data-id="element-594">
            <ResponsiveContainer width="100%" height={360} data-id="element-595">
              <LineChart data={data} margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 20
            }} data-id="element-596">
                <CartesianGrid strokeDasharray="3 3" stroke="#1A2B4A10" data-id="element-597" />
                <XAxis dataKey="month" tick={{
                fill: '#1A2B4A',
                fontSize: 11
              }} angle={-35} textAnchor="end" height={55} interval={0} data-id="element-598" />
                <YAxis tickFormatter={formatYAxis} tick={{
                fill: '#1A2B4A',
                fontSize: 12
              }} data-id="element-599" />
                <Tooltip content={<CustomTooltip data-id="element-601" />} data-id="element-600" />
                <ReferenceLine x="3rd Mo" stroke="#4ECDC4" strokeDasharray="4 4" label={{
                value: '$100K+',
                position: 'top',
                fill: '#4ECDC4',
                fontSize: 11,
                fontWeight: 700
              }} data-id="element-602" />
                <ReferenceLine x="12th Mo" stroke="#4ECDC4" strokeDasharray="4 4" label={{
                value: '$1M+',
                position: 'top',
                fill: '#4ECDC4',
                fontSize: 11,
                fontWeight: 700
              }} data-id="element-603" />
                <Line type="monotone" dataKey="withSystem" stroke="#4ECDC4" strokeWidth={3} dot={false} name="withSystem" data-id="element-604" />
                <Line type="monotone" dataKey="withoutSystem" stroke="#1A2B4A" strokeWidth={2} strokeDasharray="6 4" dot={false} name="withoutSystem" data-id="element-605" />
              </LineChart>
            </ResponsiveContainer>
            <div className="flex items-center justify-center gap-6 mt-4" data-id="element-606">
              <div className="flex items-center gap-2" data-id="element-607">
                <div className="w-6 h-0.5 bg-[#4ECDC4]" data-id="element-608" />
                <span className="text-xs text-[#1A2B4A]/60 font-medium" data-id="element-609">
                  With Recruiting System
                </span>
              </div>
              <div className="flex items-center gap-2" data-id="element-610">
                <div className="w-6 h-0" style={{
                borderTop: '2px dashed #1A2B4A',
                height: 0
              }} data-id="element-611" />
                <span className="text-xs text-[#1A2B4A]/60 font-medium" data-id="element-612">
                  Without a Recruiting System
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12" data-id="element-613">
          {[{
          value: '$100K+',
          label: 'Projected profit, first 90 days'
        }, {
          value: '50+',
          label: 'Hires on pace, month one'
        }, {
          value: '$1M+',
          label: 'Projected 12-month revenue'
        }].map((stat, i) => <motion.div key={stat.value} custom={i + 2} variants={fadeUp} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="bg-white rounded-2xl shadow-md p-6 text-center" data-id="element-614">
              <p className="text-3xl font-black text-[#1A2B4A] mb-1" data-id="element-615">
                {stat.value}
              </p>
              <p className="text-sm text-[#1A2B4A]/60" data-id="element-616">{stat.label}</p>
            </motion.div>)}
        </div>

        <motion.div custom={5} variants={fadeUp} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="flex justify-center" data-id="element-617">
          <CTAButtonLight data-id="element-618" />
        </motion.div>
      </div>
    </section>;
}